import ProjectsDuoColorIcon from "@assets/icons/projects-duo-color.svg?react";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { GroupDetailsInfoBar } from "@components/group-details/group-details-info-bar";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { BaseCompanyIdProps, GroupId } from "@custom-types/base-types";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useFetchProjects } from "@hooks/projects/use-fetch-projects";
import { requiredProjectTableColumns } from "@pages/projects/projects-utils";

interface Props extends BaseCompanyIdProps, Partial<BaseGroupDetailsProps> {
  /** ID of the group */
  groupId: GroupId;

  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;

  /** The state of projects to display (active or archived) */
  projectState: ProjectArchivingState;
}

/**
 * Contains the Projects tab of the group details - can display both active and archived projects
 */
export function GroupProjects({
  companyId,
  groupId,
  group,
  isLoading = false,
  projectState,
}: Props): JSX.Element {
  const {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    sortBy,
    setSortBy,
    loadMoreProjects,
  } = useFetchProjects({
    companyId,
    projectArchivingState: projectState,
    groupId,
  });

  const shouldShowEmptyPage =
    !isLoading &&
    !isFetchingProjects &&
    hasFetchedFirstPage &&
    projects.length === 0;

  return (
    <>
      <GroupDetailsInfoBar group={group} />
      {shouldShowEmptyPage && (
        <EmptyPage
          title={`This group has no ${projectState} projects`}
          subtitle={
            projectState === ProjectArchivingState.active
              ? "You don't have any active projects connected to this group. Create a new project or add an existing active project to this group."
              : "There are no archived projects connected to this group."
          }
          icon={ProjectsDuoColorIcon}
        />
      )}
      {!shouldShowEmptyPage && (
        <ColumnPreferencesProvider
          tableKey="group-project-table"
          columns={requiredProjectTableColumns}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: [
              "name",
              "projectManager",
              "group",
              "created",
              "lastUpdated",
              "accessLevel",
            ],
          }}
        >
          <ProjectsTable
            companyId={companyId}
            projects={projects}
            isLoading={isFetchingProjects || isLoading}
            groupId={groupId}
            projectArchivingState={projectState}
            nextPageCursor={nextPageCursor}
            loadMoreProjects={loadMoreProjects}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </ColumnPreferencesProvider>
      )}
    </>
  );
}
